'use client';

import { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { useToast } from '@/hooks/use-toast';
import { guardarCecopDia, setCecopDiaActivo, sendServerAnnouncement } from '@/lib/actions';
import { detallesDelDia } from '@/lib/programa-ss';
import { ProcesionMap, isCalibrated, ESCUDO_RING } from '@/components/cecop/ProcesionMap';
import { construirMapaDelDia, sincronizarHermandades, COLOR_POR_DEFECTO } from '@/components/cecop/cecop-dia';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  ArrowLeft, Check, Crop, Eraser, Globe, Loader2, Megaphone, Move, Radio, Save, Send, Church,
  RefreshCw, Users, Youtube, MapPin, AlertTriangle,
} from 'lucide-react';
import type {
  CecopDia, CecopDiaHermandad, CecopDiaLive, CecopMapSnapshot, CecopViewport,
  DiaSS, JartinetHermandad, ModCortejoInfo, ModPasoEntity,
} from '@/lib/types';

/**
 * CECOP de un DÍA de Semana Santa: el mapa del día con los recorridos de todas
 * sus cofradías y, encima, el GPS en tiempo real.
 *
 * Lo que se sigue **no son jugadores** sino cosas del servidor: la ENTIDAD del
 * paso (su UUID no cambia aunque se releve la cuadrilla) y el CORTEJO (su
 * primera fila es, literalmente, la cruz de guía). Por eso los desplegables
 * listan entidades y cortejos, no gente.
 */
export function CecopDiaEditor({
  dia, cecop, mapCfg, hermandades, pasosServidor, cortejosServidor, servidorOnline, onBack,
}: {
  dia: DiaSS;
  cecop: CecopDia | null;
  mapCfg: CecopMapSnapshot | null;
  hermandades: JartinetHermandad[];
  pasosServidor: ModPasoEntity[];
  cortejosServidor: ModCortejoInfo[];
  servidorOnline: boolean;
  onBack: () => void;
}) {
  const { toast } = useToast();

  const [titulo, setTitulo] = useState(cecop?.titulo || dia.nombredia || 'Día de Semana Santa');
  const [descripcion, setDescripcion] = useState(cecop?.descripcion || '');
  const [youtubeUrl, setYoutubeUrl] = useState(cecop?.youtubeUrl || '');
  const [viewport, setViewport] = useState<CecopViewport | null>(cecop?.viewport ?? null);
  // Ajuste fino del encaje de los recorridos sobre el mapa (ver `CecopDia.mapAjuste`).
  const [ajuste, setAjuste] = useState<{ dx: number; dy: number }>(
    () => ({ dx: Number(cecop?.mapAjuste?.dx) || 0, dy: Number(cecop?.mapAjuste?.dy) || 0 }),
  );
  const [previewCrop, setPreviewCrop] = useState(false);
  const [recortando, setRecortando] = useState(false);
  const [filas, setFilas] = useState<CecopDiaHermandad[]>(() => sincronizarHermandades(cecop, dia, hermandades));
  const [seleccion, setSeleccion] = useState<string | null>(null);
  /** Hermandad cuyo recorrido se está arrastrando sobre el mapa (o `null`). */
  const [moviendo, setMoviendo] = useState<string | null>(null);
  const [live, setLive] = useState<CecopDiaLive | null>(null);
  const [saving, setSaving] = useState(false);
  const [activando, setActivando] = useState(false);

  const activo = !!cecop?.activo;
  const map = mapCfg || cecop?.map || null;

  // Seguimiento en vivo (lo escribe el latido del mod, ~15 s).
  useEffect(() => {
    const u = onSnapshot(doc(db, 'cecop_dia_live', dia.id), (snap) =>
      setLive(snap.exists() ? ({ id: snap.id, ...(snap.data() as any) }) : null));
    return () => u();
  }, [dia.id]);

  const detalles = useMemo(() => detallesDelDia(dia), [dia]);

  // El documento tal y como quedaría guardado: es lo que se pinta, así que lo
  // que se ve en el mapa es exactamente lo que verá la portada.
  const borrador: CecopDia = useMemo(
    () => ({
      ...(cecop || ({} as CecopDia)),
      id: dia.id,
      titulo,
      nombredia: dia.nombredia,
      tipo: dia.tipo,
      fecha: dia.fecha,
      descripcion,
      youtubeUrl,
      activo,
      map,
      viewport,
      mapAjuste: ajuste,
      hermandades: filas,
    }),
    [cecop, dia, titulo, descripcion, youtubeUrl, activo, map, viewport, ajuste, filas],
  );

  // Mientras se mueve un recorrido manda esa cofradía: las demás se ven tenues, que es
  // lo que deja ver dónde estás poniendo el que arrastras.
  const resaltada = moviendo || seleccion;

  const { rutas, entries } = useMemo(
    () => construirMapaDelDia({ cecop: borrador, dia, hermandades, live, seleccion: resaltada }),
    [borrador, dia, hermandades, live, resaltada],
  );

  const setFila = (hermandadId: string, patch: Partial<CecopDiaHermandad>) =>
    setFilas((prev) => prev.map((f) => (f.hermandadId === hermandadId ? { ...f, ...patch } : f)));

  /**
   * Corre el recorrido de UNA cofradía. `dx`/`dy` van en % de la imagen y se SUMAN a lo
   * que ya tuviera; con `absoluto` se fija en vez de sumar (para el «quitar ajuste»).
   */
  const moverFila = (hermandadId: string, dx: number, dy: number, absoluto = false) =>
    setFilas((prev) => prev.map((f) => {
      if (f.hermandadId !== hermandadId) return f;
      const nx = absoluto ? dx : (Number(f.ajuste?.dx) || 0) + dx;
      const ny = absoluto ? dy : (Number(f.ajuste?.dy) || 0) + dy;
      // Redondeado a 3 decimales: un % de una imagen de 2000 px son 20 px, así que la
      // milésima ya es sub-píxel y no tiene sentido guardar más.
      const ajuste = { dx: Math.round(nx * 1000) / 1000, dy: Math.round(ny * 1000) / 1000 };
      // Sin desplazamiento, FUERA la clave: no se guarda ruido, y `undefined` dentro de
      // un array rompería la escritura en Firestore.
      if (ajuste.dx === 0 && ajuste.dy === 0) {
        const { ajuste: _quitado, ...resto } = f;
        return resto as CecopDiaHermandad;
      }
      return { ...f, ajuste };
    }));

  const filaMovida = filas.find((f) => f.hermandadId === moviendo) || null;
  const nombreMovida = hermandades.find((h) => h.id === moviendo)?.nombre || filaMovida?.nombre || '';

  const setPaso = (hermandadId: string, index: number, patch: Partial<CecopDiaHermandad['pasos'][number]>) =>
    setFilas((prev) => prev.map((f) => (f.hermandadId === hermandadId
      ? { ...f, pasos: f.pasos.map((p) => (p.index === index ? { ...p, ...patch } : p)) }
      : f)));

  const handleSave = async () => {
    setSaving(true);
    const r = await guardarCecopDia(dia.id, {
      titulo: titulo.trim() || dia.nombredia,
      nombredia: dia.nombredia,
      tipo: dia.tipo,
      fecha: dia.fecha ?? null,
      descripcion,
      youtubeUrl: youtubeUrl.trim(),
      // El mapa se COPIA (imagen + calibración) porque `mod_map_config` es
      // solo-admin y el panel de la portada lo ve gente sin cuenta.
      map,
      viewport,
      mapAjuste: ajuste,
      hermandades: filas,
    });
    setSaving(false);
    toast({ title: r.success ? 'Guardado' : 'Error', description: r.message, variant: r.success ? undefined : 'destructive' });
  };

  const handleActivar = async () => {
    setActivando(true);
    // Guardar antes de activar: si no, se publicaría lo de la última vez.
    if (!activo) await handleSave();
    const r = await setCecopDiaActivo(dia.id, !activo);
    setActivando(false);
    toast({ title: r.success ? 'Listo' : 'Error', description: r.message, variant: r.success ? undefined : 'destructive' });
  };

  const asignados = filas.reduce(
    (n, f) => n + (f.cortejoId != null ? 1 : 0) + f.pasos.filter((p) => p.entityUuid).length,
    0,
  );
  const total = filas.reduce((n, f) => n + 1 + f.pasos.length, 0);

  return (
    <div className="space-y-5 pb-12">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1.5"><ArrowLeft className="h-4 w-4" /> Volver</Button>
        <h1 className="flex-1 truncate text-2xl font-bold">{titulo || dia.nombredia}</h1>
        {activo && (
          <span className="flex items-center gap-1.5 rounded-full border border-rose-500/40 bg-rose-500/20 px-3 py-1 text-xs font-bold text-rose-300">
            <Radio className="h-3.5 w-3.5 animate-pulse" /> ACTIVO EN PORTADA
          </span>
        )}
        <Button onClick={handleSave} disabled={saving} variant="secondary" className="gap-2">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar
        </Button>
        <Button onClick={handleActivar} disabled={activando} variant={activo ? 'destructive' : 'default'} className="gap-2">
          {activando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />}
          {activo ? 'Desactivar' : 'ACTIVAR'}
        </Button>
      </div>

      {!isCalibrated(map) && (
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-200">
          El mapa del servidor no está calibrado. Sin eso no se pueden situar ni los recorridos ni el GPS:
          ve a <strong>Mapa Servidor</strong>, sube la imagen y calibra las dos esquinas.
        </div>
      )}

      {!servidorOnline && (
        <div className="flex items-start gap-2 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            El servidor principal no está reportando ahora mismo. Las listas de pasos y cortejos son las del
            último latido; si están vacías, es que el servidor está apagado o que el jar todavía no manda esta
            información.
          </span>
        </div>
      )}

      <div className="grid gap-5 xl:grid-cols-[1fr_400px]">
        {/* Mapa del día con todos los recorridos */}
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-white/8 bg-card/40 p-3">
            <span className="flex items-center gap-1.5 text-sm font-semibold">
              <MapPin className="h-4 w-4 text-primary" /> {filas.length} cofradía{filas.length === 1 ? '' : 's'}
            </span>
            <span className="text-xs text-muted-foreground">· {asignados}/{total} puestos asignados</span>

            <span className="mx-1 h-5 w-px bg-white/10" />
            <Button
              size="sm"
              variant={recortando ? 'default' : 'secondary'}
              onClick={() => { setRecortando((v) => !v); setPreviewCrop(false); }}
              className="gap-1.5"
            >
              <Crop className="h-3.5 w-3.5" /> Recorte GPS
            </Button>
            {viewport && (
              <>
                <Button size="sm" variant={previewCrop ? 'default' : 'outline'} onClick={() => { setPreviewCrop((v) => !v); setRecortando(false); }} className="gap-1.5">
                  <Crop className="h-3.5 w-3.5" /> {previewCrop ? 'Ver mapa completo' : 'Ver recorte'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setViewport(null); setPreviewCrop(false); }} className="gap-1.5 text-xs">
                  <Eraser className="h-3.5 w-3.5" /> Quitar recorte
                </Button>
              </>
            )}

            <Button
              size="sm"
              variant="ghost"
              onClick={() => { setFilas(sincronizarHermandades(borrador, dia, hermandades)); toast({ title: 'Sincronizado', description: 'Las cofradías del día se han vuelto a leer del programa.' }); }}
              className="ml-auto gap-1.5 text-xs"
              title="Vuelve a leer del programa qué cofradías salen y cuántos pasos llevan, conservando lo ya asignado."
            >
              <RefreshCw className="h-3.5 w-3.5" /> Sincronizar con el programa
            </Button>
          </div>

          <p className="text-xs text-muted-foreground">
            {recortando
              ? 'Arrastra sobre el mapa para elegir el trozo que se verá en el GPS de la portada.'
              : moviendo
                ? 'Arrastra el recorrido resaltado sobre el mapa hasta que caiga en sus calles.'
                : 'Los recorridos salen del Programa de Semana Santa: si retocas un trazado allí, aquí cambia solo. Pulsa una cofradía en el panel para resaltar el suyo.'}
          </p>

          {/* Barra del modo MOVER: solo sale mientras se está arrastrando una cofradía. */}
          {moviendo && (
            <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-primary/40 bg-primary/10 p-3">
              <span className="flex items-center gap-1.5 text-sm font-semibold">
                <Move className="h-4 w-4 text-primary" /> Moviendo: {nombreMovida}
              </span>
              <span className="text-[0.7rem] text-muted-foreground">
                {filaMovida?.ajuste
                  ? `X ${filaMovida.ajuste.dx} · Y ${filaMovida.ajuste.dy} (% de la imagen)`
                  : 'sin mover todavía'}
              </span>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="secondary" className="h-7 w-7 p-0" title="Izquierda" onClick={() => moverFila(moviendo, -0.1, 0)}>←</Button>
                <Button size="sm" variant="secondary" className="h-7 w-7 p-0" title="Derecha" onClick={() => moverFila(moviendo, 0.1, 0)}>→</Button>
                <Button size="sm" variant="secondary" className="h-7 w-7 p-0" title="Arriba" onClick={() => moverFila(moviendo, 0, -0.1)}>↑</Button>
                <Button size="sm" variant="secondary" className="h-7 w-7 p-0" title="Abajo" onClick={() => moverFila(moviendo, 0, 0.1)}>↓</Button>
              </div>
              <Button size="sm" variant="ghost" className="gap-1.5 text-xs" onClick={() => moverFila(moviendo, 0, 0, true)}>
                <Eraser className="h-3.5 w-3.5" /> Devolver a su sitio
              </Button>
              <Button size="sm" className="ml-auto gap-1.5" onClick={() => setMoviendo(null)}>
                <Check className="h-3.5 w-3.5" /> Hecho
              </Button>
              <p className="w-full text-[0.65rem] text-muted-foreground">
                Mueve <strong>solo el recorrido de esta cofradía</strong> (se suma al encaje general de
                abajo). No toca el GPS ni el trazado del Programa: es un desplazamiento del día, y se
                guarda con el botón <strong>Guardar</strong> de arriba.
              </p>
            </div>
          )}

          <ProcesionMap
            map={map}
            viewport={previewCrop ? viewport : null}
            color={ESCUDO_RING}
            rutas={rutas}
            live={entries}
            showLabels
            interactive={!recortando && !moviendo}
            dragSelect={recortando}
            onDragSelect={(rect) => { setViewport(rect); setRecortando(false); setPreviewCrop(true); }}
            moveDrag={!!moviendo}
            onMoveDrag={(d) => { if (moviendo) moverFila(moviendo, d.dx, d.dy); }}
          />

          <AjusteEncaje ajuste={ajuste} onChange={setAjuste} />
        </div>

        {/* Panel derecho */}
        <div className="space-y-4">
          <div className="space-y-3 rounded-2xl border border-white/8 bg-card/40 p-4">
            <h3 className="font-semibold">Datos del día</h3>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Título</Label>
              <Input value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder={dia.nombredia} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Descripción</Label>
              <Textarea value={descripcion} onChange={(e) => setDescripcion(e.target.value)} rows={2} placeholder="Opcional" />
            </div>
            <div className="space-y-1">
              <Label className="flex items-center gap-1.5 text-xs text-muted-foreground"><Youtube className="h-3.5 w-3.5" /> Directo de YouTube</Label>
              <Input value={youtubeUrl} onChange={(e) => setYoutubeUrl(e.target.value)} placeholder="https://www.youtube.com/watch?v=..." />
              <p className="text-[0.65rem] text-muted-foreground">
                Vale cualquier formato (watch, youtu.be, /live/). Se incrusta silenciado en la portada.
              </p>
            </div>
            <p className="rounded-lg border border-white/8 bg-muted/20 p-2 text-[0.65rem] text-muted-foreground">
              <strong className="text-foreground">ACTIVAR</strong> hace dos cosas a la vez: enciende el seguimiento
              GPS (el servidor solo manda posiciones de los días activos) y saca el panel del día en la portada
              pública. Solo debería haber un día activo a la vez.
            </p>
          </div>

          <AvisoBossbar hermandades={filas} />

          <div className="space-y-3 rounded-2xl border border-white/8 bg-card/40 p-4">
            <h3 className="flex items-center gap-2 font-semibold">
              <Users className="h-4 w-4 text-primary" /> Puestos del día
              <span className="ml-auto text-xs font-normal text-muted-foreground">{asignados}/{total}</span>
            </h3>
            <p className="text-xs text-muted-foreground">
              El paso se sigue por su <strong>entidad</strong> (aunque cambie el costalero, el GPS no se pierde) y
              la cruz de guía por el <strong>cortejo</strong> (se sigue su primera fila).
            </p>

            {filas.length === 0 && (
              <p className="rounded-lg border border-dashed border-white/10 py-6 text-center text-xs text-muted-foreground">
                Este día no tiene cofradías en el programa.
              </p>
            )}

            {filas.map((f) => {
              const hermandad = hermandades.find((h) => h.id === f.hermandadId);
              const detalle = detalles.find((d) => d.hermandadId === f.hermandadId);
              const tieneRecorrido = (detalle?.recorrido?.ida || []).length > 1;
              const suyas = (live?.entries || []).filter((e) => e.hermandadId === f.hermandadId);
              const chip = (role: 'cruz' | 'paso', index: number) => {
                const e = suyas.find((x) => x.role === role && x.index === index);
                if (!e) return null;
                return (
                  <span className={cn('ml-auto shrink-0 rounded-full px-1.5 py-0.5 text-[0.55rem] font-bold',
                    e.online ? 'bg-emerald-500/20 text-emerald-300' : 'bg-zinc-600/20 text-zinc-400')}>
                    {e.online ? 'GPS' : 'sin señal'}
                  </span>
                );
              };

              return (
                <div
                  key={f.hermandadId}
                  className={cn(
                    'space-y-2 rounded-xl border bg-muted/20 p-3 transition-colors',
                    moviendo === f.hermandadId
                      ? 'border-primary bg-primary/10'
                      : seleccion === f.hermandadId ? 'border-primary/60' : 'border-white/8',
                  )}
                  // Mientras se mueve una, el resaltado no se lo puede robar el ratón.
                  onMouseEnter={() => { if (!moviendo) setSeleccion(f.hermandadId); }}
                  onMouseLeave={() => { if (!moviendo) setSeleccion(null); }}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-full border-2"
                      style={{ background: '#0a0a0c', borderColor: f.color || COLOR_POR_DEFECTO }}
                    >
                      {hermandad?.escudoUrl
                        ? <img src={hermandad.escudoUrl} alt="" className="h-5 w-5 object-contain" />
                        : <Church className="h-4 w-4 text-white/50" />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold">{hermandad?.nombre || f.nombre}</span>
                      {!tieneRecorrido && (
                        <span className="block text-[0.6rem] text-amber-300">Sin recorrido en el programa</span>
                      )}
                      {f.ajuste && (
                        <span className="block text-[0.6rem] text-primary">
                          Recorrido movido · X {f.ajuste.dx} · Y {f.ajuste.dy}
                        </span>
                      )}
                    </span>
                    {/* Mover SOLO el recorrido de esta cofradía sobre el mapa. Es opcional:
                        si el mapa está bien, no hace falta tocarlo. */}
                    <Button
                      size="icon"
                      variant={moviendo === f.hermandadId ? 'default' : 'ghost'}
                      title={moviendo === f.hermandadId
                        ? 'Terminar de mover este recorrido'
                        : 'Mover SOLO el recorrido de esta cofradía sobre el mapa'}
                      disabled={!tieneRecorrido && moviendo !== f.hermandadId}
                      onClick={() => setMoviendo((m) => (m === f.hermandadId ? null : f.hermandadId))}
                      className="h-7 w-7 shrink-0"
                    >
                      <Move className="h-3.5 w-3.5" />
                    </Button>
                    <input
                      type="color"
                      value={/^#[0-9a-fA-F]{6}$/.test(f.color) ? f.color : COLOR_POR_DEFECTO}
                      onChange={(e) => setFila(f.hermandadId, { color: e.target.value })}
                      title="Color del recorrido y del filete de sus marcadores"
                      className="h-7 w-8 shrink-0 cursor-pointer rounded-md border border-white/10 bg-transparent"
                    />
                  </div>

                  {/* Cruz de guía = primera fila de un cortejo del servidor */}
                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[0.65rem] font-bold uppercase tracking-wide text-muted-foreground">✚ Cruz de guía</span>
                      {chip('cruz', 0)}
                    </div>
                    <select
                      value={f.cortejoId != null ? String(f.cortejoId) : ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        const c = cortejosServidor.find((x) => String(x.id) === v);
                        setFila(f.hermandadId, {
                          cortejoId: v === '' ? null : Number(v),
                          cortejoLabel: c ? `Cortejo ${c.id}${c.owner ? ` · ${c.owner}` : ''}` : '',
                        });
                      }}
                      className="w-full rounded-md border border-white/10 bg-background px-2 py-1.5 text-sm"
                    >
                      <option value="">— Sin cortejo —</option>
                      {/* El cortejo guardado puede haber desaparecido (las
                          formaciones viven en memoria y no sobreviven a un
                          reinicio del servidor): se sigue mostrando para no
                          borrarlo sin querer al guardar. */}
                      {f.cortejoId != null && !cortejosServidor.some((c) => c.id === f.cortejoId) && (
                        <option value={String(f.cortejoId)}>Cortejo {f.cortejoId} (ya no existe)</option>
                      )}
                      {cortejosServidor.map((c) => (
                        <option key={c.id} value={String(c.id)}>
                          Cortejo {c.id}{c.filas ? ` · ${c.filas} filas` : ''}{c.owner ? ` · ${c.owner}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Un desplegable por paso, atado a la ENTIDAD */}
                  {f.pasos.map((p) => (
                    <div key={p.index} className="space-y-1">
                      <div className="flex items-center gap-1.5">
                        <Input
                          value={p.label}
                          onChange={(e) => setPaso(f.hermandadId, p.index, { label: e.target.value })}
                          className="h-6 flex-1 text-xs"
                          placeholder={`Paso ${p.index}`}
                        />
                        {chip('paso', p.index)}
                      </div>
                      <select
                        value={p.entityUuid}
                        onChange={(e) => {
                          const ent = pasosServidor.find((x) => x.id === e.target.value);
                          setPaso(f.hermandadId, p.index, {
                            entityUuid: e.target.value,
                            entityName: ent?.name || '',
                          });
                        }}
                        className="w-full rounded-md border border-white/10 bg-background px-2 py-1.5 text-sm"
                      >
                        <option value="">— Sin asignar —</option>
                        {p.entityUuid && !pasosServidor.some((x) => x.id === p.entityUuid) && (
                          <option value={p.entityUuid}>{p.entityName || 'Paso asignado'} (no cargado)</option>
                        )}
                        {pasosServidor.map((x) => (
                          <option key={x.id} value={x.id}>
                            {x.name}{x.rider ? ` · lo lleva ${x.rider}` : ''} ({x.x}, {x.z})
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * AJUSTE FINO del encaje de los recorridos sobre el mapa.
 *
 * <p><b>Por qué hace falta</b>: los recorridos del programa se trazan sobre el
 * mapa de CIUDAD (`city_map_config/main.mapImageUrl`) y aquí se pintan sobre el
 * mapa del SERVIDOR (`mod_map_config/main.imageUrl`). Si las dos imágenes no son
 * exactamente el mismo fichero —un reencuadre, un margen distinto al exportar—,
 * TODOS los trazados salen corridos y siempre en la misma dirección.</p>
 *
 * <p><b>Lo definitivo es subir la misma imagen a los dos sitios</b> (Mapa de la
 * ciudad y Mapa Servidor) y volver a calibrar; esto es la corrección para cuando
 * no lo son. Va en % de la imagen, así que un 0,5 ya se nota.</p>
 */
function AjusteEncaje({
  ajuste, onChange,
}: {
  ajuste: { dx: number; dy: number };
  onChange: (v: { dx: number; dy: number }) => void;
}) {
  const PASO = 0.25;
  const nudge = (dx: number, dy: number) =>
    onChange({
      dx: Math.round((ajuste.dx + dx) * 100) / 100,
      dy: Math.round((ajuste.dy + dy) * 100) / 100,
    });
  const puesto = ajuste.dx !== 0 || ajuste.dy !== 0;

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-white/8 bg-card/40 p-3">
      <span className="flex items-center gap-1.5 text-sm font-semibold">
        <Move className="h-4 w-4 text-primary" /> Encaje de los recorridos
      </span>
      <div className="flex items-center gap-1">
        <Button size="sm" variant="secondary" className="h-7 w-7 p-0" title="Mover a la izquierda" onClick={() => nudge(-PASO, 0)}>←</Button>
        <Button size="sm" variant="secondary" className="h-7 w-7 p-0" title="Mover a la derecha" onClick={() => nudge(PASO, 0)}>→</Button>
        <Button size="sm" variant="secondary" className="h-7 w-7 p-0" title="Mover hacia arriba" onClick={() => nudge(0, -PASO)}>↑</Button>
        <Button size="sm" variant="secondary" className="h-7 w-7 p-0" title="Mover hacia abajo" onClick={() => nudge(0, PASO)}>↓</Button>
      </div>
      <div className="flex items-center gap-1.5">
        <Label className="text-[0.65rem] text-muted-foreground">X</Label>
        <Input
          type="number" step={0.05} value={ajuste.dx}
          onChange={(e) => onChange({ ...ajuste, dx: Number(e.target.value) || 0 })}
          className="h-7 w-20 text-xs"
        />
        <Label className="text-[0.65rem] text-muted-foreground">Y</Label>
        <Input
          type="number" step={0.05} value={ajuste.dy}
          onChange={(e) => onChange({ ...ajuste, dy: Number(e.target.value) || 0 })}
          className="h-7 w-20 text-xs"
        />
        <span className="text-[0.65rem] text-muted-foreground">% de la imagen</span>
      </div>
      {puesto && (
        <Button size="sm" variant="ghost" className="gap-1.5 text-xs" onClick={() => onChange({ dx: 0, dy: 0 })}>
          <Eraser className="h-3.5 w-3.5" /> Quitar ajuste
        </Button>
      )}
      <p className="w-full text-[0.65rem] text-muted-foreground">
        Corre <strong>TODOS</strong> los trazados a la vez. Úsalo si salen desplazados siempre en la
        misma dirección: eso pasa cuando el <strong>Mapa de la ciudad</strong> (donde se dibujan los
        recorridos del programa) y el <strong>Mapa Servidor</strong> (el que se ve aquí) no son la
        misma imagen — lo definitivo es subir el mismo fichero a los dos sitios. Si lo que falla es el
        recorrido de <strong>una sola cofradía</strong>, usa el botón de mover (<span className="inline-flex align-middle"><Move className="h-3 w-3" /></span>)
        de su fila y arrástralo sobre el mapa. Ninguno de los dos toca el GPS.
      </p>
    </div>
  );
}

/**
 * Aviso a modo bossbar para todo el servidor. Reaprovecha el mismo camino que
 * los anuncios de hermandad (`mod_announcements` → latido → dos bossbars
 * apiladas durante 1 minuto): el mod no necesita nada nuevo.
 */
function AvisoBossbar({ hermandades }: { hermandades: CecopDiaHermandad[] }) {
  const { toast } = useToast();
  const [hermandadId, setHermandadId] = useState('');
  const [titulo, setTitulo] = useState('CECOP');
  const [mensaje, setMensaje] = useState('');
  const [enviando, setEnviando] = useState(false);

  const enviar = async () => {
    if (!mensaje.trim()) return;
    if (!hermandadId) {
      toast({ title: 'Falta la cofradía', description: 'Elige de parte de quién va el aviso.', variant: 'destructive' });
      return;
    }
    setEnviando(true);
    const r = await sendServerAnnouncement(hermandadId, mensaje.trim(), titulo.trim() || undefined);
    setEnviando(false);
    toast({ title: r.success ? 'Aviso enviado' : 'Error', description: r.message, variant: r.success ? undefined : 'destructive' });
    if (r.success) setMensaje('');
  };

  return (
    <div className="space-y-2 rounded-2xl border border-white/8 bg-card/40 p-4">
      <h3 className="flex items-center gap-2 font-semibold"><Megaphone className="h-4 w-4 text-amber-400" /> Aviso al servidor</h3>
      <p className="text-xs text-muted-foreground">
        Sale como <strong>bossbar</strong> a todos los jugadores conectados durante un minuto. Tarda lo que
        tarde el siguiente latido (hasta ~15 s).
      </p>
      <select
        value={hermandadId}
        onChange={(e) => setHermandadId(e.target.value)}
        className="w-full rounded-md border border-white/10 bg-background px-2 py-1.5 text-sm"
      >
        <option value="">— De parte de… —</option>
        {hermandades.map((h) => (
          <option key={h.hermandadId} value={h.hermandadId}>{h.nombre}</option>
        ))}
      </select>
      <Input value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Título de la barra (CECOP)" className="h-8 text-sm" />
      <Textarea value={mensaje} onChange={(e) => setMensaje(e.target.value)} rows={2} placeholder="Ej. La Carrera Oficial lleva 10 minutos de retraso." />
      <Button size="sm" onClick={enviar} disabled={enviando || !mensaje.trim()} className="w-full gap-1.5">
        {enviando ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Enviar aviso
      </Button>
    </div>
  );
}
